/** Requisitos escritos numa folha de Excel -> IDS, e o caminho inverso.
 *
 *  Muita gente escreve os requisitos de informação em Excel, porque é
 *  onde os escreve há anos. Converter uma folha num IDS, que depois se
 *  revê no editor e se valida contra o modelo, poupa passá-los à mão.
 *  A ideia está no liteAECO (IDS Generator, ainda "em desenvolvimento"
 *  lá; Apache-2.0). O formato da folha e o código são nossos.
 *
 *  Uma linha por requisito. As linhas com o mesmo nome de especificação
 *  juntam-se numa só especificação.
 *
 *  Colunas (a ordem não importa; o nome sim, sem distinguir maiúsculas):
 *    Especificação   nome da especificação (obrigatório)
 *    Esquema IFC     IFC2X3, IFC4, IFC4X3 (vários separados por vírgula;
 *                    por omissão IFC4)
 *    Aplica-se a     classe IFC: IfcWall (obrigatório)
 *    PredefinedType  opcional
 *    Tipo de requisito  Propriedade (omissão), Atributo, Classificação,
 *                    Material
 *    Pset            para Propriedade
 *    Nome            nome da propriedade, do atributo, ou o sistema de
 *                    classificação
 *    Tipo de dados   para Propriedade: IfcLabel, IfcBoolean...
 *    Valor           vazio = só tem de existir; "a | b | c" = um de;
 *                    "/regex/" = padrão; "10..20" = intervalo inclusivo
 *    Cardinalidade   Obrigatório (omissão), Opcional, Proibido
 *    Instruções      texto para quem modela
 *
 *  Nada é adivinhado: uma linha que não se percebe fica fora e o
 *  relatório diz qual e porquê. */

import type { Celula, Folha } from '@core/lib/folhas'
import type {
  Cardinality,
  Facet,
  IdsFile,
  IdsSpec,
  IdsValue,
} from './idsParse'

export interface AvisoDeFolha {
  linha: number
  motivo: string
}

const COLUNAS = {
  especificacao: ['especificação', 'especificacao', 'specification', 'spec'],
  esquema: ['esquema ifc', 'esquema', 'ifc version', 'ifcversion', 'schema'],
  aplicaSe: ['aplica-se a', 'aplica se a', 'classe', 'entity', 'applicability', 'aplicável a'],
  predefinido: ['predefinedtype', 'predefined type'],
  tipo: ['tipo de requisito', 'requisito', 'facet', 'requirement type'],
  pset: ['pset', 'property set', 'propertyset'],
  nome: ['nome', 'propriedade', 'name', 'basename', 'atributo'],
  dados: ['tipo de dados', 'datatype', 'data type'],
  valor: ['valor', 'value'],
  cardinalidade: ['cardinalidade', 'cardinality', 'obrigatoriedade'],
  instrucoes: ['instruções', 'instrucoes', 'instructions'],
} as const

type Coluna = keyof typeof COLUNAS

const CABECALHO_MODELO = [
  'Especificação', 'Esquema IFC', 'Aplica-se a', 'PredefinedType', 'Tipo de requisito',
  'Pset', 'Nome', 'Tipo de dados', 'Valor', 'Cardinalidade', 'Instruções',
]

function localizar(cabecalho: string[]): Partial<Record<Coluna, number>> {
  const out: Partial<Record<Coluna, number>> = {}
  cabecalho.forEach((h, i) => {
    const t = h.trim().toLowerCase()
    for (const [col, nomes] of Object.entries(COLUNAS) as [Coluna, readonly string[]][]) {
      if (out[col] === undefined && nomes.includes(t)) out[col] = i
    }
  })
  return out
}

/** "IfcWall" ou "IFCWALL" -> "IFCWALL". */
function classeIfc(s: string): string | null {
  const t = s.trim().toUpperCase()
  return /^IFC[A-Z0-9]+$/.test(t) ? t : null
}

/** O texto da célula Valor -> restrição do IDS. */
export function lerValor(s: string): IdsValue | null {
  const t = s.trim()
  if (t === '') return null
  const padrao = /^\/(.+)\/$/.exec(t)
  if (padrao) return { pattern: padrao[1] }
  const intervalo = /^(-?\d+(?:[.,]\d+)?)\s*\.\.\s*(-?\d+(?:[.,]\d+)?)$/.exec(t)
  if (intervalo) {
    return {
      minInclusive: Number(intervalo[1].replace(',', '.')),
      maxInclusive: Number(intervalo[2].replace(',', '.')),
    }
  }
  if (t.includes('|')) {
    const lista = t.split('|').map((x) => x.trim()).filter(Boolean)
    return lista.length > 1 ? { enumeration: lista } : { simpleValue: lista[0] ?? '' }
  }
  return { simpleValue: t }
}

const VERDADE = ['true', 'verdadeiro', 'sim', 't', '1', '.t.']
const FALSO = ['false', 'falso', 'não', 'nao', 'f', '0', '.f.']

/** No IDS os booleanos escrevem-se "true" e "false", em minúsculas (é o
 *  xs:boolean do XSD). Numa folha escreve-se TRUE, VERDADEIRO ou sim.
 *  Com o tipo de dados booleano, traduz-se; sem ele não se mexe, porque
 *  "sim" pode ser mesmo o texto pedido. */
export function normalizarBooleano(v: IdsValue, dataType: string | undefined): IdsValue {
  if (dataType !== 'IFCBOOLEAN' && dataType !== 'IFCLOGICAL') return v
  const um = (s: string) => {
    const t = s.trim().toLowerCase()
    if (VERDADE.includes(t)) return 'true'
    if (FALSO.includes(t)) return 'false'
    return s
  }
  if (v.simpleValue !== undefined) return { ...v, simpleValue: um(v.simpleValue) }
  if (v.enumeration) return { ...v, enumeration: v.enumeration.map(um) }
  return v
}

function lerCardinalidade(s: string): Cardinality | null {
  const t = s.trim().toLowerCase()
  if (t === '' || ['obrigatório', 'obrigatorio', 'required', 'sim'].includes(t)) return 'required'
  if (['opcional', 'optional'].includes(t)) return 'optional'
  if (['proibido', 'prohibited', 'não', 'nao'].includes(t)) return 'prohibited'
  return null
}

const VERSOES = ['IFC2X3', 'IFC4', 'IFC4X3_ADD2', 'IFC4X3']

export function idsDeFolha(linhas: string[][], titulo: string): { ids: IdsFile; avisos: AvisoDeFolha[] } {
  const avisos: AvisoDeFolha[] = []
  const col = localizar((linhas[0] ?? []).map((c) => c ?? ''))
  const faltam = (['especificacao', 'aplicaSe'] as Coluna[]).filter((c) => col[c] === undefined)
  if (faltam.length > 0) {
    const nomes: Partial<Record<Coluna, string>> = { especificacao: 'Especificação', aplicaSe: 'Aplica-se a' }
    throw new Error(
      `A folha não tem ${faltam.length === 1 ? 'a coluna' : 'as colunas'} ${faltam.map((c) => `"${nomes[c]}"`).join(' e ')}. Descarrega o modelo para ver o formato.`,
    )
  }
  const cel = (linha: string[], c: Coluna) => (col[c] !== undefined ? (linha[col[c]!] ?? '').trim() : '')

  const specs = new Map<string, IdsSpec>()
  for (let r = 1; r < linhas.length; r++) {
    const linha = linhas[r] ?? []
    if (linha.every((x) => (x ?? '').trim() === '')) continue
    const n = r + 1
    const nomeSpec = cel(linha, 'especificacao')
    if (!nomeSpec) {
      avisos.push({ linha: n, motivo: 'Sem nome de especificação.' })
      continue
    }
    const classe = classeIfc(cel(linha, 'aplicaSe'))
    if (!classe) {
      avisos.push({ linha: n, motivo: `"${cel(linha, 'aplicaSe')}" não é uma classe IFC (ex.: IfcWall).` })
      continue
    }
    const predef = cel(linha, 'predefinido')
    const esquemas = (cel(linha, 'esquema') || 'IFC4')
      .split(/[,;]/)
      .map((x) => x.trim().toUpperCase())
      .filter(Boolean)
    const maus = esquemas.filter((e) => !VERSOES.includes(e))
    if (maus.length > 0) {
      avisos.push({ linha: n, motivo: `Esquema desconhecido: ${maus.join(', ')}. Usa IFC2X3, IFC4 ou IFC4X3.` })
      continue
    }

    let spec = specs.get(nomeSpec)
    if (!spec) {
      spec = {
        name: nomeSpec,
        ifcVersion: esquemas,
        identifier: null,
        description: null,
        instructions: null,
        applicability: [
          {
            type: 'entity',
            name: { simpleValue: classe },
            ...(predef ? { predefinedType: { simpleValue: predef.toUpperCase() } } : {}),
          },
        ],
        requirements: [],
        applicabilityCardinality: 'required',
      }
      specs.set(nomeSpec, spec)
    } else {
      const ap = spec.applicability[0]
      if (ap.type === 'entity' && ap.name.simpleValue !== classe) {
        avisos.push({
          linha: n,
          motivo: `A especificação "${nomeSpec}" já se aplica a ${ap.name.simpleValue}; esta linha diz ${classe}. Usa outro nome de especificação.`,
        })
        continue
      }
    }

    const tipo = (cel(linha, 'tipo') || 'propriedade').toLowerCase()
    const nome = cel(linha, 'nome')
    const valorTxt = cel(linha, 'valor')
    const valor = lerValor(valorTxt)
    const cardinalidade = lerCardinalidade(cel(linha, 'cardinalidade'))
    if (!cardinalidade) {
      avisos.push({ linha: n, motivo: `Cardinalidade "${cel(linha, 'cardinalidade')}" não se percebe: Obrigatório, Opcional ou Proibido.` })
      continue
    }
    const instrucoes = cel(linha, 'instrucoes')
    const comuns = {
      cardinality: cardinalidade,
      ...(instrucoes ? { instructions: instrucoes } : {}),
    }
    let faceta: Facet
    if (tipo.startsWith('propriedade') || tipo === 'property') {
      const pset = cel(linha, 'pset')
      if (!pset || !nome) {
        avisos.push({ linha: n, motivo: 'Uma propriedade precisa do Pset e do Nome.' })
        continue
      }
      const dados = cel(linha, 'dados')
      if (dados && !classeIfc(dados)) {
        avisos.push({ linha: n, motivo: `"${dados}" não é um tipo de dados IFC (ex.: IfcLabel).` })
        continue
      }
      faceta = {
        type: 'property',
        propertySet: { simpleValue: pset },
        baseName: { simpleValue: nome },
        ...(dados ? { dataType: classeIfc(dados)! } : {}),
        ...(valor ? { value: normalizarBooleano(valor, dados ? classeIfc(dados) ?? undefined : undefined) } : {}),
        ...comuns,
      }
    } else if (tipo.startsWith('atributo') || tipo === 'attribute') {
      if (!nome) {
        avisos.push({ linha: n, motivo: 'Um atributo precisa do Nome (ex.: Name, Description).' })
        continue
      }
      faceta = { type: 'attribute', name: { simpleValue: nome }, ...(valor ? { value: valor } : {}), ...comuns }
    } else if (tipo.startsWith('classifica') || tipo === 'classification') {
      faceta = {
        type: 'classification',
        ...(nome ? { system: { simpleValue: nome } } : {}),
        ...(valor ? { value: valor } : {}),
        ...comuns,
      }
    } else if (tipo.startsWith('material')) {
      faceta = { type: 'material', ...(valor ? { value: valor } : nome ? { value: { simpleValue: nome } } : {}), ...comuns }
    } else {
      avisos.push({ linha: n, motivo: `Tipo de requisito "${cel(linha, 'tipo')}" não se percebe: Propriedade, Atributo, Classificação ou Material.` })
      continue
    }
    spec.requirements.push(faceta)
  }

  const hoje = new Date()
  const ids: IdsFile = {
    info: {
      title: titulo || 'Requisitos importados de Excel',
      description: null,
      author: null,
      date: `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`,
      version: '1.0',
      purpose: null,
    },
    specs: [...specs.values()],
  }
  if (ids.specs.length === 0) throw new Error('A folha não tem nenhuma linha que se consiga ler.')
  return { ids, avisos }
}

function textoDoValor(v: IdsValue | undefined): string {
  if (!v) return ''
  if (v.simpleValue !== undefined) return v.simpleValue
  if (v.enumeration) return v.enumeration.join(' | ')
  if (v.pattern) return `/${v.pattern}/`
  if (v.minInclusive !== undefined && v.maxInclusive !== undefined) return `${v.minInclusive}..${v.maxInclusive}`
  return '(restrição que a folha não representa)'
}

const CARD_TXT: Record<Cardinality, string> = { required: 'Obrigatório', optional: 'Opcional', prohibited: 'Proibido' }

/** Um IDS numa folha, no mesmo formato. Especificações que a folha não
 *  consegue representar (aplicabilidade com várias facetas) saem com
 *  uma nota, para não se perder informação em silêncio. */
export function idsParaFolhas(ids: IdsFile): Folha[] {
  const linhas: Celula[][] = [CABECALHO_MODELO]
  const notas: Celula[][] = [['Especificação', 'Nota']]
  for (const s of ids.specs) {
    const ent = s.applicability.find((f) => f.type === 'entity')
    if (s.applicability.length !== 1 || !ent || ent.type !== 'entity') {
      notas.push([s.name ?? '', 'A aplicabilidade tem mais do que uma classe; a folha só guarda a classe.'])
    }
    const classe = ent && ent.type === 'entity' ? textoDoValor(ent.name) : ''
    const predef = ent && ent.type === 'entity' ? textoDoValor(ent.predefinedType) : ''
    const base = [s.name ?? '', s.ifcVersion.join(', '), classe, predef]
    if (s.requirements.length === 0) linhas.push([...base, '', '', '', '', '', '', ''])
    for (const f of s.requirements) {
      const card = CARD_TXT[f.cardinality ?? 'required']
      const inst = 'instructions' in f ? (f.instructions ?? '') : ''
      if (f.type === 'property') {
        linhas.push([...base, 'Propriedade', textoDoValor(f.propertySet), textoDoValor(f.baseName), f.dataType ?? '', textoDoValor(f.value), card, inst])
      } else if (f.type === 'attribute') {
        linhas.push([...base, 'Atributo', '', textoDoValor(f.name), '', textoDoValor(f.value), card, inst])
      } else if (f.type === 'classification') {
        linhas.push([...base, 'Classificação', '', textoDoValor(f.system), '', textoDoValor(f.value), card, inst])
      } else if (f.type === 'material') {
        linhas.push([...base, 'Material', '', '', '', textoDoValor(f.value), card, inst])
      } else {
        notas.push([s.name ?? '', `Requisito do tipo ${f.type} não cabe na folha.`])
      }
    }
  }
  const folhas: Folha[] = [{ nome: 'Requisitos', linhas }]
  if (notas.length > 1) folhas.push({ nome: 'Notas', linhas: notas })
  return folhas
}

/** O modelo para preencher, com três exemplos e a explicação. */
export function modeloDeRequisitos(): Folha[] {
  return [
    {
      nome: 'Requisitos',
      linhas: [
        CABECALHO_MODELO,
        ['Paredes', 'IFC4', 'IfcWall', '', 'Propriedade', 'Pset_WallCommon', 'IsExternal', 'IfcBoolean', '', 'Obrigatório', 'Indicar se a parede é exterior'],
        ['Paredes', 'IFC4', 'IfcWall', '', 'Propriedade', 'Pset_WallCommon', 'FireRating', 'IfcLabel', 'EI30 | EI60 | EI90', 'Obrigatório', ''],
        ['Portas', 'IFC4', 'IfcDoor', '', 'Atributo', '', 'Name', '', '', 'Obrigatório', ''],
      ],
    },
    {
      nome: 'Leia-me',
      linhas: [
        ['Requisitos de informação em Excel, para converter em IDS no VN2R openBIM Hub.'],
        ['Uma linha por requisito. Linhas com o mesmo nome de especificação juntam-se numa especificação.'],
        ['Especificação e Aplica-se a são obrigatórias. Esquema IFC por omissão: IFC4.'],
        ['Tipo de requisito: Propriedade (omissão), Atributo, Classificação ou Material.'],
        ['Valor: vazio = só tem de existir; "a | b" = um destes; "/regex/" = padrão; "10..20" = intervalo inclusivo.'],
        ['Cardinalidade: Obrigatório (omissão), Opcional ou Proibido.'],
        ['O IDS é a norma da buildingSMART (Information Delivery Specification). Revê o resultado no editor antes de o partilhar.'],
      ],
    },
  ]
}
